import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { accountingApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

type Tab = 'rates' | 'revaluations';

/**
 * Foreign exchange — FR-ACC-08
 * Track FX rates and run period-end revaluations.
 */
export function FxPage() {
  const [tab, setTab] = useState<Tab>('rates');

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'rates',        label: 'Exchange rates' },
    { key: 'revaluations', label: 'Revaluations' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Accounting · Treasury"
        title="Foreign exchange"
        subtitle="Maintain FX rates and post period-end unrealized gain/loss revaluations."
      />

      <div className="card p-1.5 mb-6 inline-flex gap-1 border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={clsx(
              'px-4 py-1.5 text-sm font-semibold rounded-pill transition-all',
              tab === t.key
                ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
                : 'text-slate-600 hover:bg-slate-50'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'rates' && <RatesTab />}
      {tab === 'revaluations' && <RevaluationsTab />}
    </div>
  );
}

// ============================================================
// Rates tab
// ============================================================
function RatesTab() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const listQuery = useQuery({ queryKey: ['fx-rates'], queryFn: () => accountingApi.listFxRates() });

  const kpis = useMemo(() => {
    const rows = listQuery.data ?? [];
    const pairs = new Set<string>();
    const currencies = new Set<string>();
    let mostRecent: Date | null = null;
    for (const r of rows) {
      pairs.add(`${r.from_currency}→${r.to_currency}`);
      currencies.add(r.from_currency);
      currencies.add(r.to_currency);
      const d = new Date(r.effective_date);
      if (!mostRecent || d > mostRecent) mostRecent = d;
    }
    return {
      total: rows.length,
      pairs: pairs.size,
      currencies: currencies.size,
      mostRecent: mostRecent ? mostRecent.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—',
    };
  }, [listQuery.data]);

  return (
    <div>
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total rates on file" value={kpis.total} />
        <KpiTile label="Unique pairs" value={kpis.pairs} accent="finance" />
        <KpiTile label="Currencies" value={kpis.currencies} />
        <KpiTile label="Most recent" value={kpis.mostRecent} />
      </div>

      <div className="flex justify-end mb-4">
        <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add rate</button>
      </div>

      {listQuery.isLoading ? (
        <div className="card"><LoadingState rows={4} /></div>
      ) : listQuery.error ? (
        <ErrorState error={listQuery.error} />
      ) : !listQuery.data?.length ? (
        <div className="card">
          <EmptyState
            message="No FX rates recorded"
            hint="Record rates here to support multi-currency invoicing and revaluations."
            action={
              <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add rate</button>
            }
          />
        </div>
      ) : (
        <div className="card overflow-hidden border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Effective date</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Pair</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Rate</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(listQuery.data ?? []).map((r: any, i: number) => (
                  <tr key={r.id} className={i % 2 ? 'bg-slate-50/40' : 'bg-white'}>
                    <td className="px-4 py-2 font-mono text-xs text-slate-700 tabular-nums">
                      {new Date(r.effective_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-2">
                      <span className="font-mono text-xs font-semibold text-slate-900">{r.from_currency}</span>
                      <span className="text-slate-400 mx-1.5">→</span>
                      <span className="font-mono text-xs font-semibold text-slate-900">{r.to_currency}</span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">
                      {parseFloat(r.rate).toFixed(6)}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">{r.source ?? <span className="text-slate-400 italic">manual</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAdd && <AddRateModal
        onClose={() => setShowAdd(false)}
        onAdded={() => { qc.invalidateQueries({ queryKey: ['fx-rates'] }); setShowAdd(false); }}
      />}
    </div>
  );
}

function AddRateModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [fromCurrency, setFromCurrency] = useState('EUR');
  const [toCurrency, setToCurrency] = useState('USD');
  const [rate, setRate] = useState(1);
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));

  const mut = useMutation({
    mutationFn: () => accountingApi.recordFxRate({
      fromCurrency: fromCurrency.toUpperCase(),
      toCurrency: toCurrency.toUpperCase(),
      rate, effectiveDate,
    }),
    onSuccess: () => { toast.success('Rate recorded'); onAdded(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-finance-700 mb-1">Accounting · Treasury</div>
        <h2 className="text-xl font-bold font-display text-slate-900">Record FX rate</h2>
        <p className="text-sm text-slate-500 mt-1">
          Stored with effective date. Historical rates are preserved — new entries don't overwrite.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">From currency</label>
            <input
              className="input uppercase font-mono font-semibold text-center tracking-wider"
              maxLength={3}
              value={fromCurrency}
              onChange={(e) => setFromCurrency(e.target.value.toUpperCase())}
            />
          </div>
          <div>
            <label className="label">To currency</label>
            <input
              className="input uppercase font-mono font-semibold text-center tracking-wider"
              maxLength={3}
              value={toCurrency}
              onChange={(e) => setToCurrency(e.target.value.toUpperCase())}
            />
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="font-mono text-sm text-slate-700">
              1 <span className="font-semibold text-slate-900">{fromCurrency || '…'}</span>
              <span className="mx-1.5 text-slate-400">=</span>
            </div>
            <input
              type="number" step="any" min={0}
              className="input tabular-nums text-right font-mono font-semibold !w-40"
              value={rate}
              onChange={(e) => setRate(parseFloat(e.target.value) || 0)}
            />
            <div className="font-mono text-sm font-semibold text-slate-900">{toCurrency || '…'}</div>
          </div>
          <div className="text-2xs text-slate-500 text-center">
            Up to 6 decimal precision
          </div>
        </div>

        <div>
          <label className="label">Effective date</label>
          <input
            type="date"
            className="input"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
          />
        </div>
      </div>

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          disabled={!fromCurrency || !toCurrency || rate <= 0 || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Saving…' : 'Record rate'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Revaluations tab
// ============================================================
function RevaluationsTab() {
  const qc = useQueryClient();
  const [showRun, setShowRun] = useState(false);
  const listQuery = useQuery({ queryKey: ['fx-revaluations'], queryFn: () => accountingApi.listFxRevaluations() });

  const kpis = useMemo(() => {
    const rows = listQuery.data ?? [];
    let totalGL = 0, gains = 0, losses = 0;
    for (const r of rows) {
      const gl = parseFloat(r.total_gain_loss);
      totalGL += gl;
      if (gl >= 0) gains++; else losses++;
    }
    return { total: rows.length, totalGL, gains, losses };
  }, [listQuery.data]);

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Revaluations posted" value={kpis.total} />
        <KpiTile label="Cumulative G/L" value={`${kpis.totalGL >= 0 ? '+' : ''}$${kpis.totalGL.toFixed(2)}`}
          accent={kpis.totalGL >= 0 ? 'sage' : 'danger'} />
        <KpiTile label="Gains" value={kpis.gains} accent="sage" />
        <KpiTile label="Losses" value={kpis.losses}
          accent={kpis.losses > 0 ? 'danger' : 'default'} />
      </div>

      <div className="flex justify-end mb-4">
        <button className="btn-primary" onClick={() => setShowRun(true)}>Run revaluation</button>
      </div>

      {listQuery.isLoading ? (
        <div className="card"><LoadingState rows={4} /></div>
      ) : listQuery.error ? (
        <ErrorState error={listQuery.error} />
      ) : !listQuery.data?.length ? (
        <div className="card">
          <EmptyState
            message="No revaluations run yet"
            hint="Run at period-end to post unrealized FX gain/loss for all non-base-currency balances."
            action={
              <button className="btn-primary" onClick={() => setShowRun(true)}>Run revaluation</button>
            }
          />
        </div>
      ) : (
        <div className="card overflow-hidden border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">As of</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Pair</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Rate</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Gain / Loss</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">JE</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(listQuery.data ?? []).map((r: any, i: number) => {
                  const gl = parseFloat(r.total_gain_loss);
                  return (
                    <tr key={r.id} className={i % 2 ? 'bg-slate-50/40' : 'bg-white'}>
                      <td className="px-4 py-2 font-mono text-xs text-slate-700 tabular-nums">
                        {new Date(r.as_of_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-2">
                        <span className="font-mono text-xs font-semibold text-slate-900">{r.from_currency}</span>
                        <span className="text-slate-400 mx-1.5">→</span>
                        <span className="font-mono text-xs font-semibold text-slate-900">{r.base_currency}</span>
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-sm text-slate-700 tabular-nums">
                        {parseFloat(r.rate_used).toFixed(6)}
                      </td>
                      <td className={clsx(
                        'px-4 py-2 text-right font-mono font-semibold tabular-nums',
                        gl >= 0 ? 'text-sage-700' : 'text-danger'
                      )}>
                        {gl >= 0 ? '+' : ''}${gl.toFixed(2)}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-900">
                        {r.journal_entry_number ?? <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showRun && <RunRevaluationModal
        onClose={() => setShowRun(false)}
        onRan={() => { qc.invalidateQueries({ queryKey: ['fx-revaluations'] }); setShowRun(false); }}
      />}
    </div>
  );
}

function RunRevaluationModal({ onClose, onRan }: { onClose: () => void; onRan: () => void }) {
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [currenciesText, setCurrenciesText] = useState('');
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => accountingApi.runFxRevaluation({
      asOfDate, baseCurrency,
      currencies: currenciesText.trim()
        ? currenciesText.split(',').map((c) => c.trim().toUpperCase()).filter(Boolean)
        : undefined,
      notes: notes || undefined,
    }),
    onSuccess: (d) => {
      if (d.results.length === 0) toast(d.message ?? 'No revaluation posted');
      else toast.success(`${d.results.length} currenc${d.results.length === 1 ? 'y' : 'ies'} revalued · Total $${d.totalGainLoss.toFixed(2)}`, { duration: 8000 });
      onRan();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-finance-700 mb-1">Revaluation · Treasury</div>
        <h2 className="text-xl font-bold font-display text-slate-900">Run FX revaluation</h2>
        <p className="text-sm text-slate-500 mt-2 leading-relaxed">
          Finds all GL accounts in non-base currencies, compares book value to FC balance × current rate, and posts the delta as unrealized FX gain/loss.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">As of date</label>
            <input
              type="date"
              className="input"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Base currency</label>
            <input
              className="input uppercase font-mono font-semibold text-center tracking-wider"
              maxLength={3}
              value={baseCurrency}
              onChange={(e) => setBaseCurrency(e.target.value.toUpperCase())}
            />
          </div>
        </div>

        <div>
          <label className="label">Restrict to currencies <span className="font-normal text-slate-400 normal-case tracking-normal">(optional, comma-separated)</span></label>
          <input
            className="input font-mono uppercase"
            value={currenciesText}
            onChange={(e) => setCurrenciesText(e.target.value)}
            placeholder="EUR, GBP"
          />
          <div className="text-2xs text-slate-500 mt-1.5">Leave blank to revalue all non-base currencies.</div>
        </div>

        <div>
          <label className="label">Notes <span className="font-normal text-slate-400 normal-case tracking-normal">(optional)</span></label>
          <input
            className="input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Month-end Q1 close"
          />
        </div>
      </div>

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Revaluing…' : 'Run revaluation'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Shared
// ============================================================
function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'finance' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-slate-200',
    finance: 'bg-finance-500',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden border-slate-200">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-slate-500">{label}</div>
      <div className="mt-3 text-2xl font-bold font-display text-slate-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
